import Sheet, { type SheetType } from "@core-ui/utils/Sheet";
import parseNumber from "@ext/markdown/elements/table/edit/logic/parseNumber";
import {
	type AggregationCell,
	AggregationMethod,
	AlignEnumTypes,
	type ColumnData,
} from "@ext/markdown/elements/table/edit/model/tableTypes";
import { type RefObject, useEffect, useRef, useState } from "react";

const NULL_VALUE = "-";

const getNumericData = (data: ColumnData): number[] => {
	const result: number[] = [];
	for (let i = 0; i < data.length; i++) {
		const num = parseNumber(data[i]);
		if (Number.isFinite(num)) result.push(num);
	}
	return result;
};

const AGGREGATIONS_FUNCS = {
	[AggregationMethod.SUM]: (data: ColumnData) => {
		const numericData = getNumericData(data);
		if (!numericData.length) return 0;
		let sum = 0;
		for (let i = 0; i < numericData.length; i++) sum += numericData[i];
		return sum;
	},
	[AggregationMethod.AVG]: (data: ColumnData) => {
		const numericData = getNumericData(data);
		if (!numericData.length) return 0;
		let sum = 0;
		for (let i = 0; i < numericData.length; i++) sum += numericData[i];
		return sum / numericData.length;
	},
	[AggregationMethod.MIN]: (data: ColumnData) => {
		const numericData = getNumericData(data);
		return numericData.length ? Math.min(...numericData) : 0;
	},
	[AggregationMethod.MAX]: (data: ColumnData) => {
		const numericData = getNumericData(data);
		return numericData.length ? Math.max(...numericData) : 0;
	},
	[AggregationMethod.COUNT]: (data: ColumnData) => data.length,
	[AggregationMethod.COUNT_DISTINCT]: (data: ColumnData) => new Set(data).size,
};

let cachedFormatter: Intl.NumberFormat;
export const getFormatter = () => {
	if (!cachedFormatter) {
		cachedFormatter = new Intl.NumberFormat(navigator.languages, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		});
	}
	return cachedFormatter;
};
export const getFormattedValue = (formatter: Intl.NumberFormat, value: number) => formatter.format(value);
export const getAggregatedValue = (method: AggregationMethod, data: ColumnData) => {
	if (!method || !data.length) return null;

	const filteredData = filterData(data);
	if (!filteredData.length) return null;

	const aggregatedValue = AGGREGATIONS_FUNCS[method](filteredData);

	return aggregatedValue;
};

const filterData = (data: ColumnData): ColumnData => data.filter((d) => d?.length);

const isAggregatedTable = (table: HTMLTableSectionElement) => {
	const rowToCheck = table.rows[0];
	if (!rowToCheck) return false;

	for (let i = 0; i < rowToCheck.cells?.length; i++) {
		const child = rowToCheck.cells?.[i];
		if (child?.getAttribute("aggregation")) return true;
	}

	return false;
};

export const getCellsInColumn = (table: HTMLTableSectionElement, colIndex: number): HTMLTableCellElement[] => {
	const cells: HTMLTableCellElement[] = [];

	for (let i = 0; i < table.rows.length; i++) {
		cells.push(table.rows[i].cells[colIndex]);
	}

	return cells;
};

const computeCells = (table: HTMLTableSectionElement): AggregationCell[] => {
	if (!isAggregatedTable(table)) return null;

	const tableEl = table.closest("table") as HTMLTableElement;
	const hasHeader = tableEl?.dataset.header !== "none";
	const sheet = convertHtmlTableToSheet(table, hasHeader);
	const maxCols = table.rows[0]?.cells.length || 0;
	const formatter = getFormatter();

	const cells: AggregationCell[] = [];

	for (let physicalIndex = 0; physicalIndex < maxCols; physicalIndex++) {
		const isMerged = sheet.isCellMerged(0, physicalIndex);
		const masterCell = isMerged ? sheet.getMasterCell(0, physicalIndex) : null;

		if (masterCell && masterCell.column !== physicalIndex) continue;

		const methodAttribute = table.rows[0]?.cells[physicalIndex]?.getAttribute("aggregation");
		const align =
			table.rows[table.rows.length - 1]?.cells[physicalIndex]?.getAttribute("align") || AlignEnumTypes.LEFT;

		const method = methodAttribute !== "null" ? (methodAttribute as AggregationMethod) : null;

		let value = "";
		if (method) {
			const columnData = sheet.getColumn(physicalIndex);
			const aggregated = getAggregatedValue(method, columnData);
			value = aggregated || aggregated === 0 ? getFormattedValue(formatter, aggregated) : NULL_VALUE;
		}

		const cell: AggregationCell = { method: method, value, align, physicalIndex };

		if (masterCell && masterCell.column === physicalIndex) {
			const span = sheet.getCellSpan(masterCell.row, masterCell.column);
			if (span?.colspan > 1) cell.colspan = span.colspan;
		}

		cells.push(cell);
	}

	return cells;
};

export const useAggregation = (tableRef: RefObject<HTMLTableElement>): AggregationCell[] => {
	const [cells, setCells] = useState<AggregationCell[]>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

	useEffect(() => {
		const table = tableRef.current?.tBodies?.[0];
		if (!table) return;

		const schedule = () => {
			clearTimeout(timerRef.current);
			console.log(123);
			timerRef.current = setTimeout(() => setCells(computeCells(table)), 200);
		};

		setCells(computeCells(table));

		const observer = new MutationObserver(schedule);
		observer.observe(table, { childList: true, subtree: true, characterData: true, attributes: true });

		return () => {
			observer.disconnect();
			clearTimeout(timerRef.current);
		};
	}, [tableRef.current]);

	return cells;
};

export const convertHtmlTableToSheet = (table: HTMLTableSectionElement, hasHeader = true): Sheet<string> => {
	const startRow = hasHeader ? 1 : 0;
	let maxColumns = 0;
	let rowCount = 0;
	for (let rowIndex = startRow; rowIndex < table.rows.length; rowIndex++) {
		const htmlRow = table.rows[rowIndex];

		rowCount++;
		let colCount = 0;
		for (let cellIndex = 0; cellIndex < htmlRow.cells.length; cellIndex++) {
			const cell = htmlRow.cells[cellIndex];
			const colspan = parseInt(cell.getAttribute("colspan") || "1", 10);
			colCount += colspan;
		}
		maxColumns = Math.max(maxColumns, colCount);
	}

	const data: SheetType<string> = new Array(rowCount);
	const occupied: boolean[][] = new Array(rowCount);
	for (let i = 0; i < rowCount; i++) {
		data[i] = new Array(maxColumns).fill("");
		occupied[i] = new Array(maxColumns).fill(false);
	}

	const mergeCells: Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> = [];

	let dataRowIndex = 0;
	for (let rowIndex = startRow; rowIndex < table.rows.length; rowIndex++) {
		const htmlRow = table.rows[rowIndex];

		let dataColIndex = 0;
		for (let cellIndex = 0; cellIndex < htmlRow.cells.length; cellIndex++) {
			const cell = htmlRow.cells[cellIndex];
			const cellText = cell.textContent?.trim() || "";
			const colspan = parseInt(cell.getAttribute("colspan") || "1", 10);
			const rowspan = parseInt(cell.getAttribute("rowspan") || "1", 10);

			while (dataColIndex < maxColumns && occupied[dataRowIndex][dataColIndex]) {
				dataColIndex++;
			}

			if (dataColIndex < maxColumns) {
				data[dataRowIndex][dataColIndex] = cellText;

				for (let r = 0; r < rowspan; r++) {
					for (let c = 0; c < colspan; c++) {
						const targetRow = dataRowIndex + r;
						const targetCol = dataColIndex + c;

						if (targetRow < rowCount && targetCol < maxColumns) {
							occupied[targetRow][targetCol] = true;
							if (r !== 0 || c !== 0) {
								data[targetRow][targetCol] = "";
							}
						}
					}
				}

				if (colspan > 1 || rowspan > 1) {
					const endRow = Math.min(dataRowIndex + rowspan - 1, rowCount - 1);
					const endCol = Math.min(dataColIndex + colspan - 1, maxColumns - 1);
					mergeCells.push({
						startRow: dataRowIndex,
						startCol: dataColIndex,
						endRow,
						endCol,
					});
				}
			}

			dataColIndex++;
		}
		dataRowIndex++;
	}

	const sheet = Sheet.fromArray(data);

	for (const merge of mergeCells) {
		sheet.mergeCells(merge.startRow, merge.startCol, merge.endRow, merge.endCol);
	}

	return sheet;
};
